import React, { useEffect, useMemo, useState } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { Receipt } from "@mui/icons-material";
import { SETTLEMENT_POSITIONS_ENDPOINT, SETTLEMENTS_ENDPOINT } from '../../../../consts';
import CustomDataGrid from '../../../../components/ui/customDataGrid/customDataGrid.component';
import { formatNumber } from '../../../../utils';
import { useCRUD } from '../../../../api/crud';
import { Settlement } from '../../../../types/settlements';
import { isCommonCounterSettlement } from '../../../../utils/settlementContractTerms';
import EntityReferenceLink from '../../../../components/ui/entityReference/entityReferenceLink.component';
import { useHasPermission } from '../../../../contexts/auth';
import { equalsNumberFilterOperators } from '../../../../components/ui/customDataGrid/filterOperators';

export const SettlementPositions = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const { get } = useCRUD(SETTLEMENTS_ENDPOINT);
  const [settlement, setSettlement] = useState<Settlement | null>(null);
  const canViewEquipment = useHasPermission("settlement.view_equipment");
  const isCommonCounter = settlement ? isCommonCounterSettlement(settlement) : false;

  useEffect(() => {
    const fetchSettlement = async () => {
      if (!id) {
        setSettlement(null);
        return;
      }
      const response = await get(id);
      setSettlement(response.data ?? null);
    };

    fetchSettlement();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const columns: GridColDef[] = useMemo(() => {
    const baseColumns: GridColDef[] = [
      {
        field: "id",
        headerName: "ID",
        filterOperators: equalsNumberFilterOperators(),
      },
    ];

    if (isCommonCounter) {
      baseColumns.push({
        field: "equipment_full_name",
        headerName: t("Equipment"),
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => row.equipment_full_name || "-",
        renderCell: (params) => (
          <EntityReferenceLink
            entityType="equipment"
            entityId={params.row.equipment_id}
            label={params.row.equipment_full_name || "-"}
            canPreview={canViewEquipment}
          />
        ),
      });
    }

    baseColumns.push(
      {
        field: "counter_detail__kind",
        headerName: t("Kind"),
        filterable: false,
        valueGetter: (_value, row) => {
          return row.counter_detail?.kind_display || row.previous_counter_detail?.kind_display;
        },
      },
      {
        field: "price_detail",
        headerName: t("Net price"),
        type: "number",
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => {
          return row.price_detail.price || '-';
        },
        valueFormatter: (value) => formatNumber(value),
      },
      {
        field: "counter_detail_value",
        headerName: t("Current counter"),
        type: "number",
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => {
          return row.counter_detail?.value || '-';
        },
        valueFormatter: (value) => formatNumber(value),
      },
      {
        field: "previous_counter_detail",
        headerName: t("Previous counter"),
        type: "number",
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => {
          return row.previous_counter_detail?.value || '-';
        },
        valueFormatter: (value) => formatNumber(value),
      },
      {
        field: "included_in_rent",
        headerName: t("Included in Rent"),
        type: "number",
        sortable: false,
        filterable: false,
        valueGetter: (_value, row) => {
          return row.price_detail.included_in_rent;
        },
        valueFormatter: (value) => formatNumber(value),
      },
      {
        field: "total",
        headerName: t("Net Total"),
        type: "number",
        filterOperators: equalsNumberFilterOperators(),
        valueFormatter: (value) => formatNumber(value),
      },
      {
        field: "investigation_reasons_display",
        headerName: t("Investigation reasons"),
        sortable: false,
        filterable: false,
        valueGetter: (value, row) => value || '-',
      }
    );

    return baseColumns;
  }, [canViewEquipment, isCommonCounter, t]);

  const positionsUrl = id ? `${SETTLEMENT_POSITIONS_ENDPOINT}?settlement=${id}` : SETTLEMENT_POSITIONS_ENDPOINT;

  return (
    <CustomDataGrid
      icon={<Receipt />}
      title={t("Positions")}
      url={positionsUrl}
      gridSettingsKey="SETTLEMENT_POSITIONS"
      columns={columns}
      refreshEventName="settlementUpdated"
    />
  );
};
